import * as t from 'io-ts';

import {RPreExpression, RStringOrTemplate} from '../../settings/editors/expressions/iotsCodecs';
import {makeApiType} from './apiTypes';

/*
 * Runtime types.
 */

const RReferenceFormat = t.readonly(
  t.type({
    pattern: RStringOrTemplate,
    before: t.union([RStringOrTemplate, t.undefined]),
    after: t.union([RStringOrTemplate, t.undefined]),
  }),
);

const RAutolinker = t.readonly(
  t.type({
    id: t.Integer,
    url: t.string,
    referenceFormat: RReferenceFormat,
    description: t.union([t.undefined, t.string]),
    expressions: t.readonlyArray(RPreExpression),
    urlBuilder: RStringOrTemplate,
    createdAt: t.Integer,
  }),
);

const RAutolinkersSearch = t.readonly(
  t.type({
    items: t.readonlyArray(RAutolinker),
    total: t.number,
    limit: t.number,
  }),
);

/*
 * Types.
 */

export interface ReferenceFormatModel extends t.TypeOf<typeof RReferenceFormat> {}

export interface AutolinkerModel extends t.TypeOf<typeof RAutolinker> {}

export interface UnsavedAutolinkerModel extends Omit<AutolinkerModel, 'id' | 'url' | 'createdAt'> {}

export interface AutolinkersSearchModel extends t.TypeOf<typeof RAutolinkersSearch> {}

/*
 * IO methods.
 */

export function parseAutolinker(src: any): AutolinkerModel {
  return {
    id: src.id,
    url: src.url,
    referenceFormat: src.reference_format,
    description: src.description ?? undefined,
    expressions: src.expressions,
    urlBuilder: src.url_builder,
    createdAt: src.created_at,
  };
}

function parseAutolinkersSearch(src: any): AutolinkersSearchModel {
  return {
    items: src.items.map(parseAutolinker),
    total: src.total,
    limit: src.limit,
  };
}

export function serializeAutolinker(src: UnsavedAutolinkerModel): any {
  return {
    reference_format: src.referenceFormat,
    description: src.description ?? null,
    expressions: src.expressions,
    url_builder: src.urlBuilder,
  };
}

/*
 * API types.
 */

export const ApiAutolinker = makeApiType('autolinker', RAutolinker, parseAutolinker);
export const ApiAutolinkersSearch = makeApiType(
  'autolinkersSearch',
  RAutolinkersSearch,
  parseAutolinkersSearch,
);
